import React, { useState } from 'react';
import { Badge, Box, Button, Paper, Stack, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Broadcast } from 'modules/broadcasts/types';
import { S3_BUCKET_NAME } from 'config';
import moment from 'moment';
import { DateFormats } from 'constants/common';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { broadcastApi } from 'modules/broadcasts';
import { useMutation } from '@apollo/client';
import { PORTAL_NOTIFICATIONS_DECREMENT_UNREAD_COUNT } from 'redux/types';
import { useDispatch } from 'react-redux';
import { BroadcastTypeChip } from '../broadcastTypeChip/BroadcastTypeChip';
import TrnsactLogo from 'assets/img/trnsact-short-logo.png';
import { BroadcastToUserStatus } from '@trnsact/trnsact-shared-types';

interface Props {
  initialBroadcast: Broadcast;
}

export const BroadcastNotificationCard = ({ initialBroadcast }: Props) => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const [upsertBroadcastToUser] = useMutation(broadcastApi.upsertBroadcastToUser);

  const { handleOpen: openViewBroadcastDialog } = useModal(ModalsKeys.ViewBroadcastDialog);

  const [broadcast, setBroadcast] = useState(initialBroadcast);

  const isUnread = broadcast.broadcastToUser.status === BroadcastToUserStatus.Unread;

  const handleViewDetails = async () => {
    openViewBroadcastDialog({
      broadcastId: broadcast.broadcastId,
    });
    if (isUnread) {
      const upsertBroadcastToUserResponse = await upsertBroadcastToUser({
        variables: {
          input: {
            broadcastToUserId: broadcast.broadcastToUser.broadcastToUserId,
            broadcastId: broadcast.broadcastId,
            status: BroadcastToUserStatus.Read,
          },
        },
      });
      if (upsertBroadcastToUserResponse?.data?.broadcast?.upsertBroadcastToUser?.success) {
        const savedBroadcastToUser = upsertBroadcastToUserResponse?.data?.broadcast?.upsertBroadcastToUser?.entity;
        setBroadcast({
          ...broadcast,
          broadcastToUser: {
            ...broadcast.broadcastToUser,
            broadcastToUserId: savedBroadcastToUser.broadcastToUserId,
            status: savedBroadcastToUser.status,
          },
        });
        dispatch({ type: PORTAL_NOTIFICATIONS_DECREMENT_UNREAD_COUNT });
      }
    }
  };

  return (
    <Paper sx={{ padding: '8px 12px', maxHeight: 200 }}>
      <Stack gap="0.5rem">
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ mr: 2 }}>
            <Badge color="error" variant="dot" overlap="circular" invisible={!isUnread}>
              {!broadcast.accountId && (
                <img
                  src={TrnsactLogo}
                  alt="Trnsact Logo"
                  style={{ borderRadius: '50%', width: '40px', height: '40px' }}
                />
              )}
              {broadcast.account?.logo && (
                <img
                  src={`https://${S3_BUCKET_NAME}.s3-us-west-2.amazonaws.com/${broadcast.account.logo}`}
                  alt={broadcast.account.name!}
                  style={{ borderRadius: '50%', width: '40px', height: '40px' }}
                />
              )}
            </Badge>
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="p" fontSize="16px" fontWeight={isUnread ? 500 : 400}>
              {broadcast.account?.name}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {moment(broadcast.effectiveDateTime).format(DateFormats.Short)}
            </Typography>
          </Box>
          <Box sx={{ alignSelf: 'flex-start' }}>
            <BroadcastTypeChip type={broadcast.type} />
          </Box>
        </Box>

        <Typography variant="h6" component="p" fontSize="16px" fontWeight={isUnread ? 500 : 400}>
          {broadcast.title}
        </Typography>

        <Typography variant="body1" component="p" className={classes.preview}>
          {broadcast.bodyPreview}
        </Typography>

        <Button
          variant="text"
          color="primary"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
          onClick={handleViewDetails}
        >
          View Details
        </Button>
      </Stack>
    </Paper>
  );
};

const useStyles = makeStyles(theme => ({
  preview: {
    display: '-webkit-box',
    ['-webkit-line-clamp']: 2,
    ['-webkit-box-orient']: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '14px',
  },
}));
